import styled, { css, keyframes } from 'styled-components';

import FadeIn from '@/components/animations/FadeIn';
import { media } from '@/Shared/theme';

const slideIn = keyframes`
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(0);
	}
`;

const slideOut = keyframes`
	from {
		transform: translateX(0);
	}
	to {
		transform: translateX(-100%);
	}
`;

const fadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

const fadeOut = keyframes`
	from {
		opacity: 1;
	}
	to {
		opacity: 0;
	}
`;

export const StyledFadeWrapper = styled(FadeIn as any)`
	display: flex;
	flex-flow: column;
	height: 100%;
	position: relative;

	${media.smallScreen} {
		height: 100vh;
	}
`;

export const StyledCardDeck = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;

	a:not([class]) {
		color: var(--org-color);
	}

	${media.smallScreen} {
		flex-direction: row;
		align-items: flex-start;
	}
`;

export const StyledWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
	scroll-snap-type: x mandatory;
	width: 100%;

	${media.smallScreen} {
		overflow-x: visible;
		scroll-snap-type: none;
		width: auto;
	}
`;

export const StyledListCard = styled.div`
	background-color: #e4e4e4;
	border-radius: 6px;
	box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
	flex-direction: column;
	flex-shrink: 0;
	margin-bottom: var(--spacing-4);
	margin-right: var(--spacing-4);
	max-height: 70vh;
	overflow: auto;
	position: relative;
	scroll-snap-align: start;
	width: min(350px, calc(100vw - 2rem));

	${media.smallScreen} {
		max-height: none;
		width: 400px;
	}

	&:empty {
		display: none;
	}

	.content {
		position: relative;
		box-shadow: 0px 2px 0px -1px rgb(50 50 50 / 10%);
		display: flex;
		flex-direction: column;
		margin-bottom: var(--spacing-4);
		padding-bottom: var(--spacing-4);

		&:last-of-type {
			box-shadow: none;
			margin-bottom: 0;
			padding-bottom: 0;
		}

		span {
			margin-right: var(--spacing-2);
		}

		p,
		span {
			color: var(--body-text) !important;
		}
	}
`;

export const StyledContainer = styled.div<{ image: string; $isLoading: boolean }>`
	background-image: ${(props) => (props.$isLoading ? '' : `url(${props.image})`)};
	background-position: center;
	background-repeat: no-repeat;
	background-size: 300px;
	display: flex;
	flex-direction: column;
	margin: auto;
	min-height: 300px;
	overflow-x: hidden;
	width: 100%;

	.panel & {
		overflow: auto;
	}

	${media.smallScreen} {
		height: 100%;
		overflow: auto;
	}
`;

export const StyledSidebarBackdrop = styled.div<{ $isOpen: boolean; $isClosing: boolean }>`
	display: none;

	@media (max-width: 899px) {
		animation: ${(props) =>
			props.$isClosing
				? css`
						${fadeOut} 0.3s ease forwards
				  `
				: css`
						${fadeIn} 0.3s ease forwards
				  `};
		background-color: rgba(0, 0, 0, 0.5);
		bottom: 0;
		display: ${(props) => (props.$isOpen || props.$isClosing ? 'block' : 'none')};
		left: 0;
		position: fixed;
		right: 0;
		top: 0;
		z-index: 49;
	}
`;

export const StyledLegend = styled.div<{ $isOpen?: boolean; $isClosing?: boolean }>`
	background-color: var(--neutral-50);
	box-shadow: 0 3px 8px 0 rgb(0 0 0 / 10%);
	flex-grow: 1;
	flex-shrink: 0;
	overflow: auto;
	padding: var(--spacing-6) var(--spacing-4) var(--spacing-4);

	/* Mobile styles - slide out drawer */
	@media (max-width: 899px) {
		border-radius: 0;
		height: 100%;
		left: 0;
		max-width: 320px;
		min-width: 280px;
		position: fixed;
		top: 0;
		transform: translateX(-100%);
		width: 85%;
		z-index: 50;

		${(props) =>
			props.$isOpen &&
			!props.$isClosing &&
			css`
				animation: ${slideIn} 0.3s ease forwards;
			`}

		${(props) =>
			props.$isClosing &&
			css`
				animation: ${slideOut} 0.3s ease forwards;
			`}
	}

	/* Desktop styles - static sidebar */
	${media.smallScreen} {
		border-radius: 6px;
		height: 100%;
		margin-bottom: 0;
		margin-right: var(--spacing-4);
		max-width: calc(100% - 120px);
		min-width: 275px;
		position: static;
		transform: none;
		width: 100%;
		z-index: 2;
	}

	.sidebar-header {
		align-items: center;
		display: flex;
		justify-content: space-between;
		margin-bottom: var(--spacing-4);
		padding-bottom: var(--spacing-4);
		border-bottom: 1px solid var(--input-border);

		${media.smallScreen} {
			display: none;
		}

		h4 {
			font-family: var(--font-semibold);
			font-size: 1.125rem;
			margin: 0;
		}

		.close-sidebar-btn {
			align-items: center;
			background: transparent;
			border-radius: 6px;
			border: 1px solid var(--input-border);
			cursor: pointer;
			display: flex;
			height: 36px;
			justify-content: center;
			padding: 0;
			width: 36px;

			&:hover {
				background-color: var(--neutral-100);
			}

			svg {
				height: 16px;
				width: 16px;
			}
		}
	}

	strong {
		display: block;
		font-family: var(--font-semibold);
		margin-bottom: var(--spacing-4);
	}

	.legend-checkbox {
		display: table;
	}

	.legend-item {
		align-items: center;
		display: flex;
		margin-bottom: var(--spacing-2);

		&:last-of-type {
			margin-bottom: 0;
		}
	}

	.color-indicator {
		border-radius: 50%;
		flex-shrink: 0;
		height: 25px;
		margin-right: var(--spacing-2);
		width: 25px;
	}
`;

export const StyledMobileHeader = styled.div`
	align-items: center;
	background-color: var(--neutral-50);
	display: flex;
	gap: var(--spacing-2);
	padding: var(--spacing-4) 0;
	width: 100%;
	z-index: 18;

	${media.smallScreen} {
		display: none;
	}

	.mobile-filter-btn {
		align-items: center;
		background-color: var(--org-color);
		border-radius: 6px;
		border: none;
		color: white;
		cursor: pointer;
		display: flex;
		flex-shrink: 0;
		gap: var(--spacing-2);
		height: 42px;
		justify-content: center;
		padding: 0 var(--spacing-3);

		&:hover {
			opacity: 0.9;
		}

		svg {
			height: 20px;
			width: 20px;

			path {
				fill: white;
			}
		}

		span {
			display: none;

			@media (min-width: 480px) {
				display: inline;
			}
		}
	}

	.mobile-expand-btn {
		align-items: center;
		background-color: var(--org-color);
		border-radius: 6px;
		border: none;
		cursor: pointer;
		display: flex;
		flex-shrink: 0;
		height: 42px;
		justify-content: center;
		padding: 0;
		width: 42px;

		&:hover {
			opacity: 0.9;
		}
	}
`;

export const StyledCourseAlignment = styled.div`
	display: grid;
	flex-grow: 1;
	grid-template-columns: 1fr;
	grid-template-rows: auto 1fr;
	min-height: 50vh;
	padding: 0 var(--spacing-2) var(--spacing-4);
	width: 100%;

	&.active-legend {
		${media.smallScreen} {
			grid-template-columns: 300px 1fr;
		}
	}

	${media.smallScreen} {
		grid-template-rows: 56px auto;
		padding: var(--spacing-6) var(--spacing-2) 0 0;
		position: relative;
	}

	.panel & {
		height: 100%;
	}

	.select-wrapper {
		margin-bottom: var(--spacing-4);
	}

	.fade-wrapper {
		display: none;

		${media.smallScreen} {
			display: block;
			flex-shrink: 0;
			grid-column: 1;
			grid-row: 1/3;
			height: 0;
			margin-right: var(--spacing-2);
			opacity: 0;
			overflow-x: hidden;
			overflow-y: auto;
			padding: 0;
			visibility: hidden;
			width: 0;
		}
	}

	&.active-legend .fade-wrapper {
		${media.smallScreen} {
			height: auto;
			opacity: 1;
			padding: revert;
			visibility: visible;
			width: auto;
		}
	}

	.search-row {
		display: none;

		${media.smallScreen} {
			display: block;
			grid-column: 1/3;
			grid-row: 1;
			max-width: 350px;
			z-index: 10;
		}
	}

	&.active-legend .search-row {
		${media.smallScreen} {
			grid-column: 2;
		}
	}

	.alignment-container {
		grid-column: 1;
		grid-row: 2;

		${media.smallScreen} {
			grid-column: 1/3;
			grid-row: 2;
		}
	}

	&.active-legend .alignment-container {
		${media.smallScreen} {
			grid-column: 2;
		}
	}

	.button-group {
		display: none;

		${media.smallScreen} {
			bottom: auto;
			display: flex;
			flex-direction: row;
			position: absolute;
			right: 0;
			top: 24px;
			z-index: 10;
		}

		button {
			margin-bottom: 0;
			margin-right: var(--spacing-4);
		}
	}
`;
